import React, { memo, useCallback, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { colors } from '@pff-consumer/core-ui'
import { type SubscriptionPlanType } from '@pff-consumer/schema'
import { BorderSeparator } from '@pff-consumer/tweed-ui/atoms/border-separator/border-separator'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/analytics/hooks/useSubscribeAnalyticsEvent'
import { PaypalPayment } from './paypal-payment/paypal-payment.web'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { ApplePayPayment } from './apple-pay-payment/apple-pay-payment.web'
import { CreditCardPayment } from './credit-card-payment/credit-card-payment.web'
import { useStyles } from './one-step-checkout.styles'
import { CustomerEmail } from './customer-email/customer-email.web'
import { Button } from '../button/button.web'
import { CheckoutErrorModal } from './checkout-error-modal/checkout-error-modal.web'
import { CheckoutSummary } from '../checkout-summary/checkout-summary.web'
import { useApplePayAccess } from '../../hooks/subscribe/useApplePayAccess'

import { OneStepCheckoutProps } from './one-step-checkout.props'
import { Text, TextDescriptionVariant } from '@pff-consumer/tweed-ui/atoms/text'
import { creditCardIconProps } from '../../shared/payment'

const { useCheckoutActions, useGetIsAccountsPage } = checkoutHooks

export const OneStepCheckout = memo((props: OneStepCheckoutProps) => {
  const {
    plan,
    price,
    isLoggedIn,
    onComplete,
    onError,
    onDisabledClick,
    onRecurlyTokenInit,
    scrollToTopOfTheContent,
    isEmailValid,
    isOnCompletePending,
    isPayPalProcessing,
    isCreditCardProcessing,
    isApplePayProcessing,
    isDiscountApplied,
    showKitSummaryRow,
  } = props
  const { updatePaymentStatus } = useCheckoutActions()
  const isAccountsPage = useGetIsAccountsPage()
  const [isFormOpen, setFormOpen] = useState<boolean>(false)
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const { canShowApplePay } = useApplePayAccess()
  const styles = useStyles()
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const paymentMethodsContainerStyles = `${styles.paymentMethodsContainer} ${canShowApplePay ? styles.paymentMethodsContainerWithApplePay : ''}`

  const continueWithCard = () => {
    setFormOpen((formOpen) => !formOpen)

    if (isFormOpen) {
      scrollToTopOfTheContent()
    }

    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: 'Credit Card',
      ctaLocation: 'Single Page Checkout',
    })
  }

  const onCallbackWithRecaptcha = useCallback(
    async (recurlyToken: string, paymentType: string, selectedPlan: SubscriptionPlanType) => {
      // set Loading state
      if (recaptchaRef.current) {
        const token = await recaptchaRef.current.executeAsync()
        if (token) {
          await onComplete(recurlyToken, paymentType, token, selectedPlan)
        } else {
          // reset paymentStatus
          updatePaymentStatus(null)
        }
      } else {
        // reset paymentStatus
        updatePaymentStatus(null)
      }

      return null
    },
    [onComplete, updatePaymentStatus]
  )

  return (
    <section className={styles.oneStepCheckoutContainer}>
      {!isLoggedIn ? (
        <>
          <div className={styles.header}>
            <Text
              accessibilityRole='header'
              variant={TextDescriptionVariant.TEXT_MD_XBOLD}
            >
              Create Account and Checkout
            </Text>
          </div>
          <CustomerEmail />
        </>
      ) : (
        <>
          <Text
            accessibilityRole='header'
            variant={TextDescriptionVariant.TEXT_MD_XBOLD}
          >
            Checkout
          </Text>
          {isAccountsPage ? (
            <div className={styles.separator}>
              <BorderSeparator noMargin />
            </div>
          ) : null}
        </>
      )}
      <CheckoutSummary
        selectedPlan={plan}
        price={price}
        isDiscountApplied={isDiscountApplied}
        showKitRow={showKitSummaryRow}
      />
      <div className={paymentMethodsContainerStyles}>
        {canShowApplePay && (
          <ApplePayPayment
            isDisabled={!isEmailValid}
            isOnCompletePending={isOnCompletePending}
            onComplete={onCallbackWithRecaptcha}
            onDisabledClick={onDisabledClick}
            onError={onError}
            price={price}
            plan={plan}
            onRecurlyTokenInit={onRecurlyTokenInit}
            isPaymentProcessing={isApplePayProcessing}
            size='sm'
          />
        )}

        <PaypalPayment
          isDisabled={!isEmailValid}
          onComplete={onCallbackWithRecaptcha}
          onDisabledClick={onDisabledClick}
          onError={onError}
          onRecurlyTokenInit={onRecurlyTokenInit}
          isPaymentProcessing={isPayPalProcessing}
          size='sm'
        />
        {/* Credit card button */}
        <Button
          text='Credit Card'
          onClick={!isEmailValid ? onDisabledClick : continueWithCard}
          data-analytics-id='credit-card-button'
          disabled={isOnCompletePending}
          size='sm'
          {...creditCardIconProps}
        />
      </div>
      {/* Credit card form */}
      {isFormOpen && (
        <CreditCardPayment
          isDisabled={!isEmailValid}
          isOnCompletePending={isOnCompletePending}
          onComplete={onCallbackWithRecaptcha}
          onError={onError}
          price={price}
          plan={plan}
          onRecurlyTokenInit={onRecurlyTokenInit}
          isPaymentProcessing={isCreditCardProcessing}
          isFormOpen={isFormOpen}
        />
      )}

      {/* Use this error modal only for subscribe app */}
      {!isAccountsPage && <CheckoutErrorModal />}

      {!isLoggedIn && (
        <a
          href='/login'
          className={styles.loginLink}
          data-analytics-id='existing-account-login-link'
        >
          <Text
            variant={TextDescriptionVariant.TEXT_BASE_REGULAR}
            colorOverride={colors.loginLink}
            accessibilityRole='link'
            accessibilityLabel='Login Link'
          >
            Already have an account? Log In
          </Text>
        </a>
      )}

      <div className={isAccountsPage ? styles.recaptcha : ''}>
        <ReCAPTCHA
          ref={recaptchaRef}
          size='invisible'
          sitekey={process.env.RECAPTCHA_SITE_KEY!}
        />
      </div>
    </section>
  )
})
